/**
 * Calculate nutritional status based on energy percentage and macro balance
 * @param {number} energyPercent - Percentage of target energy achieved
 * @param {Object} macroPercentages - Macro percentages { protein, carbs, fat }
 * @returns {Object} { status: 'normal'|'warning'|'critical', reasons: string[], color: string }
 */
export const calculateNutritionalStatus = (energyPercent, macroPercentages = {}) => {
  const reasons = [];
  let energyStatus = 'normal';
  let macroStatus = 'normal';

  // Energy status check according to clinical protocol
  if (energyPercent < 50) {
    energyStatus = 'critical';
    reasons.push(`Defisit Berat (${energyPercent.toFixed(1)}%)`);
  } else if (energyPercent > 130) {
    energyStatus = 'critical';
    reasons.push(`Waspada Overfeeding (${energyPercent.toFixed(1)}%)`);
  } else if (energyPercent < 80) {
    energyStatus = 'warning';
    reasons.push(`Perlu Ditingkatkan (${energyPercent.toFixed(1)}%)`);
  } else if (energyPercent > 120) {
    energyStatus = 'warning';
    reasons.push(`Mendekati Batas Atas (${energyPercent.toFixed(1)}%)`);
  } else {
    reasons.push(`Asupan Adekuat (${energyPercent.toFixed(1)}%)`);
  }

  // Macro balance check (if provided)
  if (macroPercentages && (macroPercentages.protein || macroPercentages.carbs || macroPercentages.fat)) {
    const { protein = 0, carbs = 0, fat = 0 } = macroPercentages;
    
    const proteinNum = parseFloat(protein);
    const carbsNum = parseFloat(carbs);
    const fatNum = parseFloat(fat);

    // Protein check (10-35% recommended)
    if (proteinNum < 10 || proteinNum > 35) {
      if (proteinNum < 5 || proteinNum > 40) {
        macroStatus = 'critical';
        reasons.push(`Protein severely imbalanced (${proteinNum.toFixed(1)}%)`);
      } else {
        if (macroStatus === 'normal') macroStatus = 'warning';
        reasons.push(`Protein imbalanced (${proteinNum.toFixed(1)}%)`);
      }
    }

    // Carbs check (45-65% recommended)
    if (carbsNum < 45 || carbsNum > 65) {
      if (carbsNum < 30 || carbsNum > 75) {
        macroStatus = 'critical';
        reasons.push(`Carbs severely imbalanced (${carbsNum.toFixed(1)}%)`);
      } else {
        if (macroStatus === 'normal') macroStatus = 'warning';
        reasons.push(`Carbs imbalanced (${carbsNum.toFixed(1)}%)`);
      }
    }

    // Fat check (20-35% recommended)
    if (fatNum < 20 || fatNum > 35) {
      if (fatNum < 10 || fatNum > 45) {
        macroStatus = 'critical';
        reasons.push(`Fat severely imbalanced (${fatNum.toFixed(1)}%)`);
      } else {
        if (macroStatus === 'normal') macroStatus = 'warning';
        reasons.push(`Fat imbalanced (${fatNum.toFixed(1)}%)`);
      }
    }

    // If all macros are in range
    if (macroStatus === 'normal' && proteinNum >= 10 && proteinNum <= 35 && carbsNum >= 45 && carbsNum <= 65 && fatNum >= 20 && fatNum <= 35) {
      reasons.push('Macro balance optimal');
    }
  }

  // Overall status is the worst of energy and macro status
  let overallStatus = 'normal';
  if (energyStatus === 'critical' || macroStatus === 'critical') {
    overallStatus = 'critical';
  } else if (energyStatus === 'warning' || macroStatus === 'warning') {
    overallStatus = 'warning';
  }

  // Determine color
  const color = overallStatus === 'critical' ? 'red' : overallStatus === 'warning' ? 'yellow' : 'green';

  return {
    status: overallStatus,
    reasons,
    color
  };
};

/**
 * Get status label text
 */
export const getStatusLabel = (status) => {
  switch (status) {
    case 'critical':
      return 'Critical';
    case 'warning':
      return 'Warning';
    case 'normal':
      return 'Normal';
    default:
      return 'Unknown';
  }
};

/**
 * Get status color classes for Tailwind
 */
export const getStatusColorClasses = (color) => {
  switch (color) {
    case 'red':
      return {
        dot: 'bg-red-500',
        text: 'text-red-700',
        bg: 'bg-red-50',
        border: 'border-red-300'
      };
    case 'yellow':
      return {
        dot: 'bg-yellow-500',
        text: 'text-yellow-700',
        bg: 'bg-yellow-50',
        border: 'border-yellow-300'
      };
    case 'green':
      return {
        dot: 'bg-green-500',
        text: 'text-green-700',
        bg: 'bg-green-50',
        border: 'border-green-300'
      };
    default:
      return {
        dot: 'bg-gray-500',
        text: 'text-gray-700',
        bg: 'bg-gray-50',
        border: 'border-gray-300'
      };
  }
};
